import { CameraModeSchema, FocusSchema, IdSchema, TransformSchema } from "@uzverse/core";
import { z } from "zod";
import { BuildingStyleSchema, CompanyBrandingSchema, FloorThemeSchema } from "./branding.js";
/**
 * World viewer v0 (docs/PHASE2_PLAN.md package 1): the read-only org tree the 3D client renders.
 * The node serves one company's tree at `GET /v1/users/:userId/org-tree`; the gateway merges the
 * user's companies at `GET /api/v1/world/org`. Layout (where buildings stand) is client-side in v0.
 */
export const WORLD_ROUTES = {
    /** GET: the user's companies with their buildings, floors, leads and workers. */
    org: "/api/v1/world/org",
    /** GET → `WorldSceneSnapshot`: the live scene over HTTP (first paint, tests). */
    scene: "/api/v1/world/scene",
    /** POST `WorldToolInvokeBody` → `{ output, correlationId? }`: a human UI action as a world tool call (`correlationId` goes to `history.undo`). */
    toolsInvoke: "/api/v1/world/tools/invoke",
    /** GET → `WorldCapabilities`: what the signed-in user may do in the world (drives UI handles). */
    capabilities: "/api/v1/world/capabilities",
    /** GET: an approved asset blob (immutable cache, exact content type). */
    asset: "/api/v1/assets/:sha256",
};
export const WorldToolInvokeBodySchema = z.object({
    name: z.string().min(1).max(200),
    input: z.unknown(),
    focus: FocusSchema.nullable().optional(),
});
/**
 * What the signed-in user may do in the world. The server still checks every call; this only
 * drives which handles the UI shows. `cameraModes` lists the modes the platform defines (the
 * client offers the ones it implements).
 */
export const WorldCapabilitiesSchema = z.object({
    cameraModes: z.array(CameraModeSchema),
    defaultCameraMode: CameraModeSchema,
    /** `world.map.edit` at world scope: move any building, edit world-owned land. */
    canEditMap: z.boolean(),
    /** Companies whose buildings the user may restyle (`world.style` at the company). */
    stylableCompanyIds: z.array(IdSchema),
    /** Parcels the user may arrange buildings inside. */
    arrangeableParcelIds: z.array(IdSchema),
    /** The user's own parcels (owned by a company they belong to). */
    parcels: z.array(z.object({
        id: IdSchema,
        companyId: IdSchema.nullable(),
        name: z.string().optional(),
        bounds: z.object({
            minX: z.number(),
            minZ: z.number(),
            maxX: z.number(),
            maxZ: z.number(),
        }),
    })),
});
/** Node route (service token): one company's org tree for a human. */
export const NODE_ORG_TREE_ROUTE = "/v1/users/:userId/org-tree";
export const WorldAgentSchema = z.object({
    id: IdSchema,
    name: z.string(),
    title: z.string().optional(),
    /** Role template key (`architect`, `interior_designer`) when the agent was hired from one. */
    roleKey: z.string().optional(),
    /** `lead` or `worker`; present on the org tree so UIs can group without guessing. */
    kind: z.enum(["lead", "worker"]).optional(),
});
export const WorldFloorSchema = z.object({
    id: IdSchema,
    name: z.string(),
    level: z.number().int(),
    /** Look of the floor (3D interior theme). Absent = generic. */
    theme: FloorThemeSchema.optional(),
    /** Department key the floor belongs to (free slug, for example `software`). */
    department: z.string().optional(),
    lead: WorldAgentSchema.nullable(),
    workers: z.array(WorldAgentSchema),
});
export const WorldBuildingSchema = z.object({
    id: IdSchema,
    name: z.string(),
    footprint: z.object({ width: z.number().positive(), depth: z.number().positive() }),
    transform: TransformSchema.nullable(),
    version: z.number().int().positive(),
    style: BuildingStyleSchema.optional(),
    /** The company's branding, repeated on its buildings for the sign, flag and lobby wall. */
    branding: CompanyBrandingSchema.optional(),
    floors: z.array(WorldFloorSchema),
});
export const WorldCompanyTreeSchema = z.object({
    company: z.object({
        id: IdSchema,
        name: z.string(),
        branding: CompanyBrandingSchema.optional(),
        /** A demo company (kept for trying things out); sorted after real companies. */
        demo: z.boolean().optional(),
    }),
    buildings: z.array(WorldBuildingSchema),
});
export const WorldOrgResponseSchema = z.object({ companies: z.array(WorldCompanyTreeSchema) });
//# sourceMappingURL=world.js.map